import { useMutation } from '@apollo/client';
import React, { useCallback, useState } from 'react';
import Modal, { ModalBody, ModalFooter } from '../../components/Modal';
import { Flex } from 'rebass';
import Button from '../../components/Button/Button';
import PropTypes from 'prop-types';
import { Input, Label } from '@rebass/forms';
import useNotify from '../../hooks/useNotify';
import TesterPicker, { Tester } from '../../components/Tester/TesterPicker';
import { gql } from '@/modes/company/gql';

type AssignJobModalProps = {
  onClose: (actionTaken?: boolean) => void;
  assignmentIds: string[] | readonly string[];
};

const ASSIGN_JOBS = gql(`
  mutation AssignInboundAssignments(
    $assignmentIds: [ID!]!
    $vendorProfileId: ID!
    $dueDate: String
  ) {
    assignInboundAssignments(
      assignmentIds: $assignmentIds
      vendorProfileId: $vendorProfileId
      dueDate: $dueDate
    ) {
      id
      status
      child {
        id
        status
        vendorProfile {
          id
          name
        }
        due
      }
    }
  }
`);

const AssignModal: React.FC<AssignJobModalProps> = ({
  onClose,
  assignmentIds,
}) => {
  const { showError, showSuccess } = useNotify();

  const onCancel = useCallback(() => onClose(), []);
  const onActionComplete = useCallback(() => {
    showSuccess(
      `${assignmentIds.length} job${
        assignmentIds.length > 1 ? 's' : ''
      } assigned`
    );
    onClose(true);
  }, []);

  const [assignJobs, { loading: mutationLoading }] = useMutation(ASSIGN_JOBS, {
    onCompleted: onActionComplete,
    onError: useCallback((error) => {
      showError(
        `Error Assigning Job${assignmentIds.length > 1 ? 's' : ''}`,
        error
      );
    }, []),
  });

  const [vendorProfileId, setVendorProfileId] = useState<string>('');
  const onSelectTesterChange = useCallback(
    (tester: Tester | null) => {
      if (tester) {
        setVendorProfileId(tester.profileId);
      }
    },
    [setVendorProfileId]
  );

  const [dueDate, setDueDate] = useState<string>('');
  const onDueDateChange = useCallback(
    (e: any) => {
      setDueDate(e.target.value);
    },
    [setDueDate]
  );

  return (
    <Modal
      title={`Assign ${
        assignmentIds.length > 1 ? assignmentIds.length : ''
      } Job${assignmentIds.length > 1 ? 's' : ''}`}
      onClose={onClose}
    >
      <ModalBody>
        <Flex p={3} flexDirection="column">
          <Label htmlFor="tester">Select Tester</Label>
          <TesterPicker
            autoFocus={true}
            onChange={onSelectTesterChange}
            testerProfileId={vendorProfileId}
            where={{ status: 'ACTIVE' }}
          />
          <Label mt={3} htmlFor="date">
            Optional Due Date
          </Label>
          <Input
            id="date"
            name="date"
            type="date"
            onChange={onDueDateChange}
            autoComplete="off"
          />
          {mutationLoading && <p>Loading...</p>}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          icon="check-circle"
          disabled={mutationLoading}
          onClick={() => {
            assignJobs({
              variables: {
                assignmentIds: assignmentIds as string[],
                vendorProfileId: vendorProfileId,
                dueDate,
              },
            });
          }}
        >
          Assign
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onCancel}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AssignModal.propTypes = {
  onClose: PropTypes.any,
  assignmentIds: PropTypes.any.isRequired,
};

export default AssignModal;
